import JobZImage from "../../../common/jobz-img";
import SectionPagination from "../../public-user/sections/common/section-pagination";
import { useEffect, useState } from "react";
import axiosInstance from "../../../../axiosInstance";
import { API_URL } from "../../../../config";
import { Helmet } from 'react-helmet-async';
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const getColorClass = (char) => {
    if (!char) return "bg-default";
    const letter = char.toUpperCase();
    const colors = {
        A: "bg-red", B: "bg-blue", C: "bg-green", D: "bg-orange", E: "bg-purple",
        F: "bg-teal", G: "bg-pink", H: "bg-indigo", I: "bg-yellow", J: "bg-cyan",
        K: "bg-lime", L: "bg-rose", M: "bg-amber", N: "bg-violet", O: "bg-emerald",
        P: "bg-fuchsia", Q: "bg-sky", R: "bg-gray", S: "bg-zinc", T: "bg-stone",
        U: "bg-neutral", V: "bg-blue-500", W: "bg-orange-600", X: "bg-indigo-500",
        Y: "bg-pink-600", Z: "bg-teal-600",
    };
    return colors[letter] || "bg-default";
};

function CanJobFinderPage() {
    const { t } = useTranslation();
    const [country, setCountry] = useState('');
    const [city, setCity] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [loading, setLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasSearched, setHasSearched] = useState(false);
    const itemsPerPage = 5;

    const handleSearch = async () => {
        // Country required
        if (!country) {
            toast.error("Please select a country");
            return;
        }

        if (!city) {
            toast.error("Please enter city name");
            return;
        }

        // Search text required
        if (!jobTitle) {
            toast.error("Please enter job title");
            return;
        }

        setLoading(true);
        setHasSearched(true);

        try {
            const response = await axiosInstance.get(`${API_URL}/searchJobs`, {
                params: {
                    search: jobTitle,
                    city: city,
                    country: country,
                    page: currentPage
                }
            });

            if (response.data.status === 200) {
                const apiJobs = response.data.data || [];

                const mappedJobs = apiJobs.map((job) => ({
                    id: job.id,
                    title: job.title,
                    category: job.category?.label || "",
                    location: job.location?.display_name || "",
                    companyWebsite: job.redirect_url,
                    company: job.company?.display_name || "",
                    companyLogo: null,
                    min_salary: job.salary_min,
                    max_salary: job.salary_max,
                    contract_time: job.contract_time,
                    job_url: "/job-details"
                }));

                console.log("Mapped Jobs:", mappedJobs);

                setJobs(mappedJobs);
                setCurrentPage(1);

            } else {
                toast.error("No jobs found!");
            }
        } catch (error) {
            toast.error("Something went wrong while fetching jobs.");
            console.error("Search API Error:", error);
        }

        setLoading(false);
    };

    useEffect(() => {
        setCity("");
        setJobs([]);
        setHasSearched(false);
    }, [country]);

    const countryOptions = [
        { value: "UK", label: t("United Kingdom") },
        { value: "Deutschland", label: t("Germany") },
        { value: "france", label: t("France") },
        { value: "Nederland", label: t("Netherlands") },
        { value: "Italia", label: t("Italy") },
        { value: "Österreich", label: t("Austria") },
        { value: "Poland", label: t("Poland") },
        { value: "België", label: t("Belgium") },
        { value: "Schweiz", label: t("Switzerland") },
        { value: "España", label: t("Spain") },
    ];

    return (
        <>
            <Helmet>
                <title>Job Finder | CV Diffusion</title>
                <meta name="description" content="Welcome to the home page of MyApp." />
            </Helmet>

            <div className="twm-right-section-panel candidate-save-job site-bg-gray">
                <div className="panel-body wt-panel-body p-a20 m-b30">
                    <div className="row">
                        {/* Country */}
                        <div className="col-xl-3 col-lg-3 col-md-12 mb-3">
                            <label>{t("Select Country")}</label>
                            <select
                                name="country"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="form-control"
                                style={{ minHeight: "40px" }}
                            >
                                <option value="">{t("Select Country")}</option>
                                {countryOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* City */}
                        <div className="col-xl-3 col-lg-3 col-md-12 mb-3">
                            <label>{t("Search City")}</label>
                            <input
                                type="text"
                                placeholder={`${t("Enter city name")}...`}
                                className="form-control"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                        </div>

                        {/* Job Title + Button */}
                        <div className="col-xl-6 col-lg-6 col-md-12 mb-3">
                            <label>{t("Search Job Title")}</label>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <input
                                    type="text"
                                    placeholder={`${t("Enter job title")}...`}
                                    className="form-control"
                                    value={jobTitle}
                                    onChange={(e) => setJobTitle(e.target.value)}
                                    style={{ flex: 1 }}
                                />

                                <button
                                    type="button"
                                    className="site-button"
                                    onClick={handleSearch}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    {t("Search")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="twm-jobs-list-wrap">
                    {loading ? (
                        <div className="text-center my-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : jobs.length > 0 ? (
                        <ul>
                            {jobs
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map((job, index) => (
                                    <li key={index}>
                                        <div className="twm-jobs-list-style1 mb-5">
                                            <div className="twm-media">
                                                {job.companyLogo ? (
                                                    <JobZImage src={job.companyLogo} alt={job.title} />
                                                ) : (
                                                    <div className={`company-logo-placeholder ${getColorClass(job.company?.charAt(0))}`}>
                                                        {job.company?.charAt(0)?.toUpperCase() || ''}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="twm-mid-content">
                                                <a
                                                    href={job.companyWebsite}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="twm-job-title"
                                                >
                                                    <h4>
                                                        {job.title}{" "}
                                                        {job.contract_time && (
                                                            <span className="twm-job-post-duration">
                                                                / {job.contract_time.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                                            </span>
                                                        )}
                                                    </h4>
                                                </a>
                                            </div>
                                            <div className="twm-right-content">
                                                <div><span>{job.category}</span></div>
                                                <div className="twm-jobs-amount">
                                                    <span>
                                                        {job.min_salary ? `$${job.min_salary}` : ""}
                                                        {job.min_salary && job.max_salary ? " / " : ""}
                                                        {job.max_salary ? job.max_salary : ""}
                                                    </span>
                                                </div>
                                                <a
                                                    href={job.companyWebsite}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="twm-jobs-browse site-text-primary"
                                                >
                                                    {t("Apply Job")}
                                                </a>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                        </ul>
                    ) : hasSearched ? (
                        <p>{t("No jobs available.")}</p>
                    ) : null}
                </div>

                <SectionPagination
                    currentPage={currentPage}
                    totalItems={jobs.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => {
                        if (page >= 1 && page <= Math.ceil(jobs.length / itemsPerPage)) {
                            setCurrentPage(page);
                        }
                    }} />
                {/* <SectionPagination
                    currentPage={currentPage}
                    totalItems={jobs.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => handlePageChange(page)}
                /> */}
            </div>
        </>
    );
}

export default CanJobFinderPage;